import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { archiveNotification, createBelief, createCapture, createDecision, markNotificationRead, runIntelligenceSkill, updateAionFile } from "@/services/intelligence";

const DEFAULT_CAPTURE = { title: "", text: "" };
const DEFAULT_BELIEF = { belief: "", domain: "general", evidence: "" };
const DEFAULT_DECISION = { title: "", summary: "", assumptions: "", success_criteria: "", warning_signs: "", evidence: "" };

const statItems = (intelligence) => [
  { label: "Captures", value: intelligence?.captures?.length || 0, testId: "intelligence-stat-captures" },
  { label: "Connections", value: intelligence?.connections?.length || 0, testId: "intelligence-stat-connections" },
  { label: "Patterns", value: intelligence?.patterns?.length || 0, testId: "intelligence-stat-patterns" },
  { label: "Notifications", value: intelligence?.notifications?.length || 0, testId: "intelligence-stat-notifications" },
  { label: "Beliefs", value: intelligence?.beliefs?.length || 0, testId: "intelligence-stat-beliefs" },
  { label: "Decisions", value: intelligence?.decisions?.length || 0, testId: "intelligence-stat-decisions" },
];

export const PersonalIntelligencePanel = ({ intelligence, token, onRefresh, memory }) => {
  const [captureDraft, setCaptureDraft] = useState(DEFAULT_CAPTURE);
  const [beliefDraft, setBeliefDraft] = useState(DEFAULT_BELIEF);
  const [decisionDraft, setDecisionDraft] = useState(DEFAULT_DECISION);
  const [aionContent, setAionContent] = useState(intelligence?.aion_file?.content || "");
  const [statusLine, setStatusLine] = useState("Personal Intelligence OS linked to Vault, Memory, and Scheduler.");
  const [pendingAction, setPendingAction] = useState("");
  const [notificationFilter, setNotificationFilter] = useState("all");

  useEffect(() => {
    setAionContent(intelligence?.aion_file?.content || "");
  }, [intelligence?.aion_file?.content]);

  const stats = useMemo(() => statItems(intelligence), [intelligence]);
  const skills = intelligence?.scheduler?.skills || [];
  const vaultSections = intelligence?.vault?.sections || [];
  const notifications = intelligence?.notifications || [];
  const captures = intelligence?.captures || [];
  const beliefs = intelligence?.beliefs || [];
  const decisions = intelligence?.decisions || [];
  const home = intelligence?.home || {};
  const vaultKnowledge = vaultSections.find((section) => section.id === "01-KNOWLEDGE")?.notes || [];
  const vaultProjects = vaultSections.find((section) => section.id === "02-PROJECTS")?.notes || [];
  const vaultOutputs = vaultSections.find((section) => section.id === "04-OUTPUTS")?.notes || [];
  const filteredNotifications = notifications.filter((item) => {
    if (notificationFilter === "all") return true;
    if (notificationFilter === "unread") return !item.read;
    return item.category === notificationFilter || item.priority === notificationFilter;
  });

  const handleNotificationRead = async (notificationId) => {
    await runAction(`read-${notificationId}`, async () => {
      await markNotificationRead(notificationId, token);
      setStatusLine("Notification marked as read.");
    });
  };

  const handleNotificationArchive = async (notificationId) => {
    await runAction(`archive-${notificationId}`, async () => {
      await archiveNotification(notificationId, token);
      setStatusLine("Notification archived.");
    });
  };

  const runAction = async (label, action) => {
    setPendingAction(label);
    try {
      await action();
      await onRefresh();
    } catch (error) {
      setStatusLine(error.message || "Action failed");
    } finally {
      setPendingAction("");
    }
  };

  const handleCapture = async () => {
    await runAction("capture", async () => {
      const result = await createCapture({ title: captureDraft.title || undefined, text: captureDraft.text, source: "dashboard" }, token);
      setCaptureDraft(DEFAULT_CAPTURE);
      setStatusLine(`Capture processed as ${result.capture.classification}.`);
    });
  };

  const handleBelief = async () => {
    await runAction("belief", async () => {
      await createBelief({ belief: beliefDraft.belief, domain: beliefDraft.domain, evidence: beliefDraft.evidence ? [beliefDraft.evidence] : [] }, token);
      setBeliefDraft(DEFAULT_BELIEF);
      setStatusLine("Belief stored and connected to the knowledge vault.");
    });
  };

  const handleDecision = async () => {
    await runAction("decision", async () => {
      await createDecision({
        title: decisionDraft.title,
        summary: decisionDraft.summary,
        assumptions: decisionDraft.assumptions.split("\n").map((item) => item.trim()).filter(Boolean),
        success_criteria: decisionDraft.success_criteria.split("\n").map((item) => item.trim()).filter(Boolean),
        warning_signs: decisionDraft.warning_signs.split("\n").map((item) => item.trim()).filter(Boolean),
        evidence: decisionDraft.evidence.split("\n").map((item) => item.trim()).filter(Boolean),
      }, token);
      setDecisionDraft(DEFAULT_DECISION);
      setStatusLine("Decision intelligence updated with assumptions and review metadata.");
    });
  };

  const handleAionFileSave = async () => {
    await runAction("aion-file", async () => {
      await updateAionFile({ content: aionContent }, token);
      setStatusLine("AION.md operating context saved.");
    });
  };

  const triggerSkill = async (skillName) => {
    await runAction(skillName, async () => {
      const result = await runIntelligenceSkill(skillName, token);
      setStatusLine(result.summary || `${skillName} completed.`);
    });
  };

  return (
    <section className="jarvis-panel jarvis-panel-span-full intelligence-panel" data-testid="personal-intelligence-panel">
      <div className="jarvis-panel-header intelligence-header" data-testid="personal-intelligence-header">
        <div>
          <p className="jarvis-eyebrow" data-testid="personal-intelligence-kicker">Neural memory operating layer</p>
          <h2 className="jarvis-title intelligence-title" data-testid="personal-intelligence-title">AION Personal Intelligence OS</h2>
          <p className="intelligence-subtitle" data-testid="personal-intelligence-subtitle">
            Vault, Morning Brief, Weekly Synthesis, Connection Finder, Pattern Detector, Belief Tracker, Decision Intelligence und Scheduler in einem Live-Bereich.
          </p>
        </div>
        <div className="jarvis-chip intelligence-chip" data-testid="personal-intelligence-memory-chip">
          Memory context: {(memory?.recent_context || []).length} live threads
        </div>
      </div>

      <div className="intelligence-status-line" data-testid="personal-intelligence-status-line">{statusLine}</div>

      <div className="intelligence-stat-grid" data-testid="personal-intelligence-stats-grid">
        {stats.map((item) => (
          <div className="jarvis-detail-box intelligence-stat-box" key={item.label} data-testid={item.testId}>
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </div>
        ))}
      </div>

      <div className="intelligence-card intelligence-card-span-2" data-testid="home-screen-card">
        <div className="intelligence-card-header">
          <h3 data-testid="home-screen-title">{home?.greeting || "Good Morning"}</h3>
          <span className="intelligence-mini-chip" data-testid="home-screen-focus-chip">Today Focus</span>
        </div>
        <div className="intelligence-home-grid" data-testid="home-screen-grid">
          <div className="jarvis-detail-box" data-testid="today-focus-task-box"><span>Most important task</span><strong>{home?.focus?.most_important_task || "—"}</strong></div>
          <div className="jarvis-detail-box" data-testid="today-focus-risk-box"><span>Biggest risk</span><strong>{home?.focus?.biggest_risk || "—"}</strong></div>
          <div className="jarvis-detail-box" data-testid="today-focus-opportunity-box"><span>Biggest opportunity</span><strong>{home?.focus?.biggest_opportunity || "—"}</strong></div>
          <div className="jarvis-detail-box" data-testid="today-focus-project-box"><span>Needs attention</span><strong>{home?.focus?.project_needing_attention || "—"}</strong></div>
        </div>
      </div>

      <div className="intelligence-grid" data-testid="personal-intelligence-grid">
        <div className="intelligence-card" data-testid="morning-brief-card">
          <div className="intelligence-card-header">
            <h3 data-testid="morning-brief-title">Morning Brief</h3>
            <Button data-testid="morning-brief-run-button" disabled={pendingAction === "morning_brief"} onClick={() => triggerSkill("morning_brief")}>Run now</Button>
          </div>
          <p className="intelligence-summary" data-testid="morning-brief-summary">{intelligence?.morning_brief?.summary || "No morning brief generated yet."}</p>
          <div className="intelligence-list" data-testid="morning-brief-priorities-list">
            {(intelligence?.morning_brief?.top_priorities || []).slice(0, 5).map((item, index) => <div className="intelligence-list-item" key={`${item}-${index}`} data-testid={`morning-brief-priority-${index}`}>{item}</div>)}
          </div>
        </div>

        <div className="intelligence-card intelligence-card-span-2" data-testid="aion-file-card">
          <div className="intelligence-card-header">
            <h3 data-testid="aion-file-title">AION.md Operating File</h3>
            <Button data-testid="aion-file-save-button" disabled={pendingAction === "aion-file"} onClick={handleAionFileSave}>Save file</Button>
          </div>
          <Textarea value={aionContent} onChange={(event) => setAionContent(event.target.value)} className="intelligence-textarea intelligence-code-textarea" data-testid="aion-file-textarea" />
        </div>

        <div className="intelligence-card" data-testid="vault-system-card">
          <div className="intelligence-card-header">
            <h3 data-testid="vault-system-title">Vault System</h3>
            <span className="intelligence-mini-chip" data-testid="vault-system-count-chip">{vaultSections.length} sections</span>
          </div>
          <div className="vault-grid" data-testid="vault-system-grid">
            {vaultSections.map((section) => (
              <div className="vault-cell" key={section.id} data-testid={`vault-section-${section.id.toLowerCase()}`}>
                <strong>{section.id}</strong>
                <span>{section.title}</span>
                <small>{section.count} entries</small>
              </div>
            ))}
          </div>
        </div>

        <div className="intelligence-card" data-testid="capture-processor-card">
          <div className="intelligence-card-header">
            <h3 data-testid="capture-processor-title">Capture Processor</h3>
            <Button data-testid="capture-digest-run-button" disabled={pendingAction === "capture_processor"} onClick={() => triggerSkill("capture_processor")}>Digest</Button>
          </div>
          <Input value={captureDraft.title} onChange={(event) => setCaptureDraft((prev) => ({ ...prev, title: event.target.value }))} placeholder="Capture title" data-testid="capture-title-input" />
          <Textarea value={captureDraft.text} onChange={(event) => setCaptureDraft((prev) => ({ ...prev, text: event.target.value }))} placeholder="Drop an idea, task, decision, reference, or insight…" className="intelligence-textarea" data-testid="capture-textarea" />
          <Button data-testid="capture-submit-button" disabled={!captureDraft.text || pendingAction === "capture"} onClick={handleCapture}>Process capture</Button>
          <div className="intelligence-list" data-testid="capture-list">
            {captures.slice(0, 4).map((item) => (
              <div className="intelligence-list-item" key={item.id} data-testid={`capture-item-${item.id}`}>
                <strong>{item.title}</strong>
                <span>{item.classification}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="intelligence-card" data-testid="connections-card">
          <div className="intelligence-card-header">
            <h3 data-testid="connections-title">Connections</h3>
            <Button data-testid="connections-run-button" disabled={pendingAction === "connection_finder"} onClick={() => triggerSkill("connection_finder")}>Refresh</Button>
          </div>
          <div className="intelligence-list" data-testid="connections-list">
            {(intelligence?.connections || []).slice(0, 5).map((item) => (
              <div className="intelligence-list-item" key={item.id} data-testid={`connection-item-${item.id}`}>
                <strong>{item.title}</strong>
                <span>{item.description}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="intelligence-card" data-testid="patterns-card">
          <div className="intelligence-card-header">
            <h3 data-testid="patterns-title">Patterns</h3>
            <Button data-testid="patterns-run-button" disabled={pendingAction === "pattern_detector"} onClick={() => triggerSkill("pattern_detector")}>Detect</Button>
          </div>
          <div className="intelligence-list" data-testid="patterns-list">
            {(intelligence?.patterns || []).slice(0, 5).map((item) => (
              <div className="intelligence-list-item" key={item.id} data-testid={`pattern-item-${item.id}`}>
                <strong>{item.pattern_type}</strong>
                <span>{item.summary}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="intelligence-card" data-testid="beliefs-card">
          <div className="intelligence-card-header">
            <h3 data-testid="beliefs-title">Beliefs</h3>
            <Button data-testid="belief-review-run-button" disabled={pendingAction === "belief_tracker"} onClick={() => triggerSkill("belief_tracker")}>Review</Button>
          </div>
          <Input value={beliefDraft.domain} onChange={(event) => setBeliefDraft((prev) => ({ ...prev, domain: event.target.value }))} placeholder="Belief domain" data-testid="belief-domain-input" />
          <Textarea value={beliefDraft.belief} onChange={(event) => setBeliefDraft((prev) => ({ ...prev, belief: event.target.value }))} placeholder="Core belief" className="intelligence-textarea" data-testid="belief-textarea" />
          <Input value={beliefDraft.evidence} onChange={(event) => setBeliefDraft((prev) => ({ ...prev, evidence: event.target.value }))} placeholder="Initial evidence" data-testid="belief-evidence-input" />
          <Button data-testid="belief-submit-button" disabled={!beliefDraft.belief || pendingAction === "belief"} onClick={handleBelief}>Store belief</Button>
          <div className="intelligence-list" data-testid="beliefs-list">
            {beliefs.slice(0, 4).map((item) => (
              <div className="intelligence-list-item" key={item.id} data-testid={`belief-item-${item.id}`}>
                <strong>{item.belief}</strong>
                <span>{item.status}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="intelligence-card" data-testid="decisions-card">
          <div className="intelligence-card-header">
            <h3 data-testid="decisions-title">Decisions</h3>
            <Button data-testid="decision-review-run-button" disabled={pendingAction === "decision_review"} onClick={() => triggerSkill("decision_review")}>Review</Button>
          </div>
          <Input value={decisionDraft.title} onChange={(event) => setDecisionDraft((prev) => ({ ...prev, title: event.target.value }))} placeholder="Decision title" data-testid="decision-title-input" />
          <Textarea value={decisionDraft.summary} onChange={(event) => setDecisionDraft((prev) => ({ ...prev, summary: event.target.value }))} placeholder="Decision summary" className="intelligence-textarea" data-testid="decision-summary-textarea" />
          <Textarea value={decisionDraft.assumptions} onChange={(event) => setDecisionDraft((prev) => ({ ...prev, assumptions: event.target.value }))} placeholder="Assumptions (one per line)" className="intelligence-textarea intelligence-mini-textarea" data-testid="decision-assumptions-textarea" />
          <Textarea value={decisionDraft.success_criteria} onChange={(event) => setDecisionDraft((prev) => ({ ...prev, success_criteria: event.target.value }))} placeholder="Success criteria (one per line)" className="intelligence-textarea intelligence-mini-textarea" data-testid="decision-success-criteria-textarea" />
          <Textarea value={decisionDraft.warning_signs} onChange={(event) => setDecisionDraft((prev) => ({ ...prev, warning_signs: event.target.value }))} placeholder="Warning signs (one per line)" className="intelligence-textarea intelligence-mini-textarea" data-testid="decision-warning-signs-textarea" />
          <Textarea value={decisionDraft.evidence} onChange={(event) => setDecisionDraft((prev) => ({ ...prev, evidence: event.target.value }))} placeholder="Evidence (one per line)" className="intelligence-textarea intelligence-mini-textarea" data-testid="decision-evidence-textarea" />
          <Button data-testid="decision-submit-button" disabled={!decisionDraft.title || !decisionDraft.summary || pendingAction === "decision"} onClick={handleDecision}>Store decision</Button>
          <div className="intelligence-list" data-testid="decisions-list">
            {decisions.slice(0, 4).map((item) => (
              <div className="intelligence-list-item" key={item.id} data-testid={`decision-item-${item.id}`}>
                <strong>{item.title}</strong>
                <span>{item.review_status}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="intelligence-card" data-testid="weekly-synthesis-card">
          <div className="intelligence-card-header">
            <h3 data-testid="weekly-synthesis-title">Weekly Synthesis</h3>
            <Button data-testid="weekly-synthesis-run-button" disabled={pendingAction === "weekly_synthesis"} onClick={() => triggerSkill("weekly_synthesis")}>Run now</Button>
          </div>
          <p className="intelligence-summary" data-testid="weekly-synthesis-summary">{intelligence?.weekly_synthesis?.summary || "No weekly synthesis generated yet."}</p>
          <div className="intelligence-list" data-testid="weekly-synthesis-next-priorities-list">
            {(intelligence?.weekly_synthesis?.next_priorities || []).slice(0, 4).map((item, index) => <div className="intelligence-list-item" key={`${item}-${index}`} data-testid={`weekly-synthesis-priority-${index}`}>{item}</div>)}
          </div>
        </div>

        <div className="intelligence-card" data-testid="scheduler-card">
          <div className="intelligence-card-header">
            <h3 data-testid="scheduler-title">Scheduler</h3>
            <span className="intelligence-mini-chip" data-testid="scheduler-running-chip">{intelligence?.scheduler?.running ? "running" : "idle"}</span>
          </div>
          <div className="intelligence-list" data-testid="scheduler-skills-list">
            {skills.map((skill) => (
              <div className="intelligence-list-item intelligence-skill-row" key={skill.name} data-testid={`scheduler-skill-${skill.name}`}>
                <div>
                  <strong>{skill.title}</strong>
                  <span>{skill.schedule_label}</span>
                </div>
                <small>{skill.next_run_at ? new Date(skill.next_run_at).toLocaleString() : "—"}</small>
              </div>
            ))}
          </div>
        </div>

        <div className="intelligence-card" data-testid="notifications-card">
          <div className="intelligence-card-header">
            <h3 data-testid="notifications-title">Notifications</h3>
            <span className="intelligence-mini-chip" data-testid="notifications-count-chip">{notifications.length}</span>
          </div>
          <div className="intelligence-inline-actions" data-testid="notifications-filter-row">
            {[
              ["all", "All"],
              ["unread", "Unread"],
              ["brief", "Briefings"],
              ["risks", "Risks"],
              ["warning", "Warnings"],
            ].map(([value, label]) => (
              <Button key={value} variant={notificationFilter === value ? "default" : "outline"} data-testid={`notifications-filter-${value}`} onClick={() => setNotificationFilter(value)}>{label}</Button>
            ))}
          </div>
          <div className="intelligence-list" data-testid="notifications-list">
            {filteredNotifications.slice(0, 6).map((item) => (
              <div className="intelligence-list-item" key={item.id} data-testid={`notification-item-${item.id}`}>
                <strong>{item.title}</strong>
                <span>{item.message}</span>
                <small>{item.priority}</small>
                <div className="intelligence-inline-actions">
                  <Button data-testid={`notification-read-button-${item.id}`} disabled={pendingAction === `read-${item.id}`} onClick={() => handleNotificationRead(item.id)}>Mark read</Button>
                  <Button variant="outline" data-testid={`notification-archive-button-${item.id}`} disabled={pendingAction === `archive-${item.id}`} onClick={() => handleNotificationArchive(item.id)}>Archive</Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="intelligence-card" data-testid="open-tasks-card">
          <div className="intelligence-card-header">
            <h3 data-testid="open-tasks-title">Open Tasks</h3>
            <span className="intelligence-mini-chip" data-testid="open-tasks-count-chip">{(home?.open_tasks || []).length}</span>
          </div>
          <div className="intelligence-list" data-testid="open-tasks-list">
            {(home?.open_tasks || []).slice(0, 6).map((item) => (
              <div className="intelligence-list-item" key={item.id} data-testid={`open-task-item-${item.id}`}>
                <strong>{item.title}</strong>
                <span>{item.summary}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="intelligence-card" data-testid="active-projects-card">
          <div className="intelligence-card-header">
            <h3 data-testid="active-projects-title">Active Projects</h3>
            <span className="intelligence-mini-chip" data-testid="active-projects-count-chip">{(home?.active_projects || []).length}</span>
          </div>
          <div className="intelligence-list" data-testid="active-projects-list">
            {(home?.active_projects || []).slice(0, 6).map((item) => (
              <div className="intelligence-list-item" key={item.id} data-testid={`active-project-item-${item.id}`}>
                <strong>{item.title}</strong>
                <span>{item.summary}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="intelligence-card intelligence-card-span-2" data-testid="project-center-card">
          <div className="intelligence-card-header">
            <h3 data-testid="project-center-title">Project Center</h3>
            <span className="intelligence-mini-chip" data-testid="project-center-count-chip">{vaultProjects.length} tracked</span>
          </div>
          <div className="intelligence-list" data-testid="project-center-list">
            {vaultProjects.slice(0, 6).map((item, index) => (
              <div className="intelligence-list-item" key={item.id || index} data-testid={`project-center-item-${index}`}>
                <strong>{item.title}</strong>
                <span>{item.summary}</span>
                <small>Related decisions: {decisions.filter((decision) => (decision.linked_note_ids || []).includes(item.id)).length}</small>
                <small>Related memories: {(item.linked_ids || []).length}</small>
              </div>
            ))}
          </div>
        </div>

        <div className="intelligence-card" data-testid="recent-memories-card">
          <div className="intelligence-card-header">
            <h3 data-testid="recent-memories-title">Recent Memories</h3>
            <span className="intelligence-mini-chip" data-testid="recent-memories-count-chip">{(home?.recent_memories || []).length}</span>
          </div>
          <div className="intelligence-list" data-testid="recent-memories-list">
            {(home?.recent_memories || []).slice(0, 6).map((item, index) => (
              <div className="intelligence-list-item" key={`${item.recorded_at || index}-${index}`} data-testid={`recent-memory-item-${index}`}>
                <strong>{item.source || item.type || "memory"}</strong>
                <span>{item.summary || item.text || item.response || JSON.stringify(item)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="intelligence-card intelligence-card-span-2" data-testid="memory-center-card">
          <div className="intelligence-card-header">
            <h3 data-testid="memory-center-title">Memory Center</h3>
            <span className="intelligence-mini-chip" data-testid="memory-center-count-chip">Browse memories</span>
          </div>
          <div className="vault-grid" data-testid="memory-center-grid">
            <div className="vault-cell" data-testid="memory-center-projects-box">
              <strong>Projects</strong>
              {vaultProjects.slice(0, 4).map((item, index) => <small key={`${item.id || index}-project`}>{item.title}</small>)}
            </div>
            <div className="vault-cell" data-testid="memory-center-decisions-box">
              <strong>Decisions</strong>
              {decisions.slice(0, 4).map((item) => <small key={item.id}>{item.title}</small>)}
            </div>
            <div className="vault-cell" data-testid="memory-center-ideas-box">
              <strong>Ideas & Insights</strong>
              {[...captures.filter((item) => ["IDEA", "INSIGHT"].includes(item.classification)), ...vaultKnowledge].slice(0, 4).map((item, index) => <small key={`${item.id || index}-idea`}>{item.title}</small>)}
            </div>
            <div className="vault-cell" data-testid="memory-center-outputs-box">
              <strong>Outputs</strong>
              {vaultOutputs.slice(0, 4).map((item, index) => <small key={`${item.id || index}-output`}>{item.title}</small>)}
            </div>
          </div>
        </div>

        <div className="intelligence-card" data-testid="risks-card">
          <div className="intelligence-card-header">
            <h3 data-testid="risks-title">Risks</h3>
            <span className="intelligence-mini-chip" data-testid="risks-count-chip">{(home?.risks || []).length}</span>
          </div>
          <div className="intelligence-list" data-testid="risks-list">
            {(home?.risks || []).slice(0, 6).map((item) => (
              <div className="intelligence-list-item" key={item.id} data-testid={`risk-item-${item.id}`}>
                <strong>{item.pattern_type}</strong>
                <span>{item.summary}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="intelligence-card" data-testid="opportunities-card">
          <div className="intelligence-card-header">
            <h3 data-testid="opportunities-title">Opportunities</h3>
            <span className="intelligence-mini-chip" data-testid="opportunities-count-chip">{(home?.opportunities || []).length}</span>
          </div>
          <div className="intelligence-list" data-testid="opportunities-list">
            {(home?.opportunities || []).slice(0, 6).map((item) => (
              <div className="intelligence-list-item" key={item.id} data-testid={`opportunity-item-${item.id}`}>
                <strong>{item.title}</strong>
                <span>{item.description}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
